import { PDFDownloadLink } from "@react-pdf/renderer";
import { MenuItemStyled } from "pages/Orders/components/OrdersTable/components/RowActionsMenu";
import { Button } from "primereact/button";
import styled from "styled-components";

import { ClearOrderDocument, Props } from "./ClearOrderDocument";

const StyledPDFDownloadLink = styled(PDFDownloadLink)`
  text-decoration: none;
`;

type DownloadButtonProps = {
  data: Props;
  isLogoLoading?: boolean;
  /** CLE-3015 — a `TourAnchor` value for the menu item (`data-tour`). */
  tourAnchor?: string;
  /** CLE-3018 — render as a header pill button instead of a "..." menu item. */
  asButton?: boolean;
};

export function ClearOrderProDownloadButton({
  data,
  isLogoLoading = false,
  tourAnchor,
  asButton,
}: DownloadButtonProps) {
  if (asButton) {
    return (
      <div data-tour={tourAnchor} data-testid="download-pdf_OrderHeader">
        {isLogoLoading ? (
          <Button
            className="p-button-action p-button-neutral"
            icon="pi pi-spin pi-spinner"
            label="Loading..."
            disabled
          />
        ) : (
          <StyledPDFDownloadLink
            className="p-button p-component p-button-action p-button-neutral"
            data-testid="download-pdf_OrderHeader_DownloadLink"
            document={<ClearOrderDocument {...data} />}
            fileName={`clear_order__${new Date().getTime()}.pdf`}
          >
            {({ loading }: { loading: boolean }) =>
              loading ? (
                <>
                  <span className="p-button-icon p-c p-button-icon-left pi pi-spin pi-spinner" />
                  <span className="p-button-label p-c">Loading...</span>
                </>
              ) : (
                <>
                  <span className="p-button-icon p-c p-button-icon-left pi pi-download" />
                  <span className="p-button-label p-c">Download PDF</span>
                </>
              )
            }
          </StyledPDFDownloadLink>
        )}
      </div>
    );
  }
  // CLE-3015 — the anchor sits on a wrapper, not on the link: @react-pdf/renderer's PDFDownloadLink
  // (3.1.12) passes only style, className, onClick and children to its <a>, so a data-tour on it never
  // reached the DOM and the tour's Download PDF step could not find its target (dev QA of #1438). The
  // wrapper is there from the first render, loading or not, so the step finds it as soon as the menu
  // opens, and it stays the same element when the link replaces the spinner.
  return (
    <div data-tour={tourAnchor}>
      {isLogoLoading ? (
        // Show loading state while logo is being fetched
        <MenuItemStyled
          type="button"
          disabled
          data-neutral="true"
          className="flex align-items-center gap-3"
        >
          <i className="pi pi-spin pi-spinner" />
          Loading...
        </MenuItemStyled>
      ) : (
        <MenuItemStyled
          as={StyledPDFDownloadLink}
          data-testid="download-pdf_OrderHeader_DownloadLink"
          data-neutral="true"
          className="flex align-items-center gap-3"
          document={<ClearOrderDocument {...data} />}
          fileName={`clear_order__${new Date().getTime()}.pdf`}
        >
          {({ loading }: { loading: boolean }) =>
            loading ? (
              <>
                <i className="pi pi-spin pi-spinner" />
                Loading...
              </>
            ) : (
              <>
                <i className="pi pi-download" />
                Download Good Faith Estimate (PDF)
              </>
            )
          }
        </MenuItemStyled>
      )}
    </div>
  );
}
